<template>
  <div class="block">
    <div
      class="block__name"
      :class="{ active: isOpened || search }"
      @click="onClickTitle"
    >
      <div>
        {{ block.name }}
        <ui-tooltip-icon
          v-if="block.nameHelp"
          preset="help"
          :text="block.nameHelp"
        ></ui-tooltip-icon>
      </div>

      <div class="icon__wrapper">
        <ui-chevron :open="isOpened || !!search" color="inherit" size="16"></ui-chevron>
      </div>
    </div>
    <template v-for="key in block.keys">
      <div class="row" v-show="isOpened || search">
        <div class="plan-limits-key-text-short">
          {{ planLimitsKeyTextShort[key] }}
          <ui-tooltip-icon
            preset="help"
            v-if="helpTextMain({ key })"
            :text="helpTextMain({ key })"
          ></ui-tooltip-icon>
        </div>
        <div class="d-flex align-center">
          <loading-circle
            v-if="isAddonPending({ key })"
            class="loader"
            :width="12"
            :height="12"
          />
          <template v-if="!isAddonPending({ key })">
            <ui-button
              v-if="showManageAddon({ key }) && !showDetailsForAmountAddon({ key })"
              size="micro"
              :text="addonButtonConfig({ key }).text"
              :icon="addonButtonConfig({ key }).icon"
              :buttonStyle="addonButtonConfig({ key }).buttonStyle"
              @click="openAddonDialog({ planLimit: key })"
              class="mr-4"
            />
            <span
              class="mr-4 included-in-plan"
              v-if="planFullData?.limits?.[key] === true"
              >{{ t('Included in plan') }}</span
            >
            <ui-icon
              v-if="store.billing.limits[key] === true"
              :color="$colors.green"
              size="14"
              class="icon__wrapper"
              icon="far fa-check"
            />
            <ui-icon
              v-else-if="store.billing.limits[key] === false"
              :color="$colors.red"
              size="16"
              class="icon__wrapper"
              icon="far fa-times"
            />

            <span
              v-if="typeof store.billing.limits[key] === 'number'"
              :style="{
                color:
                  +store.billing.usage[key] >= +store.billing.limits[key]
                    ? $colors.red
                    : $colors.green,
              }"
            >
              <template v-if="key === 'dataRetention'">
                {{ store.billing.limits[key] }}
                {{ tc('month', store.billing.limits[key]) }}
              </template>
              <template v-else>
                {{ store.billing.usage[key] }}/{{ store.billing.limits[key] }}
              </template>
            </span>
          </template>
        </div>
      </div>
      <template v-if="showDetailsForAmountAddon({ key })">
        <div class="row ml-16">
          <div>
            {{ t('Included in plan') }}
            <ui-tooltip-icon
              preset="help"
              v-if="helpTextIncludedInPlan({ key })"
              :text="helpTextIncludedInPlan({ key })"
            ></ui-tooltip-icon>
          </div>
          <div>
            <loading-circle
              v-if="isAddonPending({ key })"
              class="loader"
              :width="12"
              :height="12"
            />
            <span
              v-if="
                !isAddonPending({ key }) &&
                typeof store.billing.limits[key] === 'number'
              "
              :style="{
                color:
                  +store.billing.usage[key] >= +store.billing.limits[key]
                    ? $colors.red
                    : $colors.green,
              }"
            >
              <template v-if="key === 'dataRetention'">
                {{
                  store.billing.limits[key] - store.billing.addons[key].addonLimit
                }}
                {{
                  tc(
                    'month',
                    store.billing.limits[key] -
                      store.billing.addons[key].addonLimit,
                  )
                }}
              </template>
              <template v-else>
                {{
                  store.billing.usage[key] - store.billing.addons[key].addonUsage
                }}/{{
                  store.billing.limits[key] - store.billing.addons[key].addonLimit
                }}
              </template>
            </span>
          </div>
        </div>
        <div class="row ml-16">
          <div>
            {{ t('Add-ons') }}
            <ui-tooltip-icon
              preset="help"
              v-if="helpTextAddons({ key })"
              :text="helpTextAddons({ key })"
            ></ui-tooltip-icon>
          </div>
          <div class="d-flex align-center">
            <loading-circle
              v-if="isAddonPending({ key })"
              class="loader"
              :width="12"
              :height="12"
            />
            <template v-if="!isAddonPending({ key })">
              <ui-button
                v-if="showManageAddon({ key })"
                size="micro"
                :text="addonButtonConfig({ key }).text"
                :icon="addonButtonConfig({ key }).icon"
                :buttonStyle="addonButtonConfig({ key }).buttonStyle"
                @click="openAddonDialog({ planLimit: key })"
                class="mr-4"
              />
              <span
                :style="{
                  color:
                    +store.billing.usage[key] >= +store.billing.limits[key]
                      ? $colors.red
                      : $colors.green,
                }"
              >
                <template v-if="key === 'dataRetention'">
                  {{ store.billing.addons[key].addonLimit }}
                  {{ tc('month', store.billing.addons[key].addonLimit) }}
                </template>
                <template v-else>
                  {{ store.billing.addons[key].addonUsage }}/{{
                    store.billing.addons[key].addonLimit
                  }}
                </template>
              </span>
            </template>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n, useColors, usePlanLimits } from '@/composables';
import { $emitter } from '@/services/emitter';
import { useStore } from '@/store';
import LoadingCircle from '@/components/loading-circle.vue';

import type { BillingPlan } from '@/services/models';

defineProps<{
  block: {
    name: string;
    nameHelp?: string;
    keys: string[];
  };
  search: string;
}>();

const store = useStore();

const planFullData = computed<BillingPlan>(
  () => store.billingPlans.find(plan => plan.id === store.billing.planId)!,
);

const $colors = useColors();
const { t, tc } = useI18n();
const { planLimitsKeyTextShort } = usePlanLimits(undefined);

const isOpened = ref(false);

const onClickTitle = () => {
  isOpened.value = !isOpened.value;
};

const helpTextMain = (payload: { key: string }) => {
  switch (payload.key) {
    case 'offersAmount':
    case 'affiliateNetworksAmount':
    case 'trafficSourcesAmount':
    case 'flowsAmount':
    case 'landingsAmount':
    case 'campaignsAmount':
      return t('Total amount of elements included in plan and add-ons.');
    case 'domainsAmount':
      return t('Total amount of tracking domains  included in plan and add-ons');
    case 'redirectDomainsAmount':
      return t('Total amount of cloaking domains  included in plan and add-ons.');
    case 'memberAdminsAmount':
      return t('Total amount of admin roles included in plan and add-ons.');
    case 'memberUsersAmount':
      return t('Total amount of user roles included in plan and add-ons.');
    case 'sharedReportsAmount':
      return t('Total amount of shared reports included in plan and add-ons.');
    case 'customColumnsAmount':
      return t('Total amount of custom columns included in plan and add-ons.');
    case 'customConversionsAmount':
      return t('Total amount of custom conversions included in plan and add-ons.');
    case 'dataRetention':
      return t('You can purchase to 12 months of data retention.');
    case 'ivtIpIsDatacenter':
      return t(
        'This metric identifies the amount of traffic that comes from the IPs from known data centers, e.g. VPN traffic.',
      );
    case 'ivtIpIsBlacklisted':
      return t(
        'This metric identifies the amount of traffic that comes from blacklisted IPs.',
      );
    case 'ivtFastClicker':
      return t(
        'This metric allows you to identify clicks on your landing page that are too fast. They have less than 800ms between ad visit and the click on CTA button. Such clicks most likely belong to bots.',
      );
    case 'ivtIpWithFrequentEvents':
      return t(
        'This metric allows you to track IPs  that are generating an unusual amount of events, which can be clarified as bot or fraudulent activity.',
      );
    case 'ivtIpWithFrequentCampaignEvents':
      return t(
        'This metric is tracking the activity from one IP address within an ad campaign. If the number of events is more than <b>60 per min</b>, traffic from such an IP is marked as suspicious.',
      );
    case 'uniqOfferViewerCondition':
      return t(
        'This option redirects the visitor who has already converted to another offer page',
      );
    case 'externalIdCondition':
      return t(
        'Allows redirecting visits without an External ID to a selected page.<br>Visits without an External ID may come from previews or bots. By default, BeMob is not accepting this type of  traffic.',
      );
    case 'bulkReportBrowserNames':
    case 'bulkReportBrowserVersions':
    case 'bulkReportCities':
    case 'bulkReportConnectionTypes':
    case 'bulkReportCountries':
    case 'bulkReportDay':
    case 'bulkReportDayOfWeek':
    case 'bulkReportDeviceModels':
    case 'bulkReportDeviceTypes':
    case 'bulkReportDeviceVendors':
    case 'bulkReportHourOfDay':
    case 'bulkReportIp':
    case 'bulkReportIsp':
    case 'bulkReportLanguages':
    case 'bulkReportMobileCarriers':
    case 'bulkReportMonth':
    case 'bulkReportOs':
    case 'bulkReportOsVersions':
    case 'bulkReportReferrerDomains':
    case 'bulkReportReferrers':
    case 'bulkReportScreenResolutions':
    case 'bulkReportStatesRegions':
    case 'bulkReportSubnetBig':
    case 'bulkReportSubnetSmall':
    case 'bulkReportUserAgents':
      return t('Available via API requests');
    default:
      return undefined;
  }
};

const helpTextIncludedInPlan = (payload: { key: string }) => {
  switch (payload.key) {
    case 'offersAmount':
    case 'affiliateNetworksAmount':
    case 'trafficSourcesAmount':
    case 'flowsAmount':
    case 'landingsAmount':
    case 'campaignsAmount':
      return t('Amount of elements included in the current plan.');
    case 'domainsAmount':
      return t('Amount of tracking domains included in the current plan.');
    case 'redirectDomainsAmount':
      return t('Amount of cloaking domains included in the current plan. ');
    case 'memberAdminsAmount':
      return t('Amount of admin roles included in the current plan.');
    case 'memberUsersAmount':
      return t('Amount of user roles included in the current plan.');
    case 'sharedReportsAmount':
      return t('Amount of shared reports included in the current plan.');
    case 'customColumnsAmount':
      return t('Amount of custom columns included in the current plan.');
    case 'customConversionsAmount':
      return t('Amount of custom conversions included in the current plan.');
    default:
      return undefined;
  }
};

const helpTextAddons = (payload: { key: string }) => {
  switch (payload.key) {
    case 'offersAmount':
    case 'affiliateNetworksAmount':
    case 'trafficSourcesAmount':
    case 'flowsAmount':
    case 'landingsAmount':
    case 'campaignsAmount':
      return t('Amount of elements purchased additionally in excess of the plan.');
    case 'domainsAmount':
      return t(
        'Amount of tracking domains purchased additionally in excess of the plan.',
      );
    case 'redirectDomainsAmount':
      return t(
        'Amount of cloaking domains purchased additionally in excess of the plan.',
      );
    case 'memberAdminsAmount':
      return t('Amount of admin roles purchased additionally in excess of the plan.');
    case 'memberUsersAmount':
      return t('Amount of user roles purchased additionally in excess of the plan.');
    case 'sharedReportsAmount':
      return t('Amount of shared reports purchased additionally in excess of the plan.');
    case 'customColumnsAmount':
      return t('Amount of custom columns purchased additionally in excess of the plan.');
    case 'customConversionsAmount':
      return t(
        'Amount of custom conversions purchased additionally in excess of the plan.',
      );
    default:
      return undefined;
  }
};

const isAddonPending = (payload: { key: string }) => {
  return store.billing.addons[payload.key]?.pending || !!store.billing.pending;
};

const isOnOff = (payload: { key: string }) => {
  const addon = store.billingAddons[payload.key] as
    | { pricing_model?: string }
    | undefined;
  return addon?.pricing_model === 'flat_fee';
};

const showDetailsForAmountAddon = (payload: { key: string }) => {
  return isOpened.value && !isOnOff(payload) && store.billing.addons[payload.key];
};

const showManageAddon = (payload: { key: string }) => {
  return planFullData.value?.limits?.[payload.key as keyof BillingLimits] !== true;
};

const addonButtonConfig = (payload: { key: string }) => {
  const isEnabledAsAddon = store.billing.addons[payload.key];
  if (!isEnabledAsAddon) {
    return {
      text: t('Buy'),
      icon: 'fas fa-badge-dollar',
      buttonStyle: 'blue',
    };
  }
  if (isEnabledAsAddon && isOnOff(payload)) {
    return {
      text: t('Switch Off'),
      icon: 'fas fa-times',
      buttonStyle: 'red',
    };
  }
  return {
    text: t('Manage'),
    icon: 'fas fa-list',
    buttonStyle: 'accent',
  };
};

const openAddonDialog = (params: { planLimit: keyof BillingLimits }) => {
  $emitter.emit('open-dialog', { name: 'ManageAddon', data: params });
};
</script>

<style lang="scss" scoped>
.block {
  width: 100%;
}

.block__name {
  font-size: 15px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: 20px 0 12px 0;
  border-bottom: 1px solid var(--color-gray10);
  &.active {
    border-bottom: 1px solid var(--color-green);
  }
  &:lang(en) {
    text-transform: capitalize;
  }
}

.plan-limits-key-text-short{
  &:lang(en) {
    text-transform: capitalize;
  }
}

.row {
  display: flex;
  height: 34px;
  box-sizing: initial;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--color-gray10);

  &:last-child {
    border-bottom: none;
  }
}

.details-list {
  &_title {
    font-weight: 600;
  }

  &_text {
    font-size: 13px;
    text-align: end;
    align-items: flex-end;
  }
}

.icon__wrapper {
  width: 14px;
  display: flex;
  justify-content: center;
}

.included-in-plan {
  color: var(--color-green);
}
</style>
